import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "flex size-full flex-col bg-white font-medium" }
const _hoisted_2 = { class: "overflow-auto" }
const _hoisted_3 = { class: "flex justify-center gap-4 border-b border-context-6 py-4" }
const _hoisted_4 = {
  key: 0,
  class: "border-b border-context-6 p-4"
}
const _hoisted_5 = { class: "t3 flex items-center text-context-4" }
const _hoisted_6 = { class: "m-0" }
const _hoisted_7 = {
  key: 1,
  class: "flex items-center border-b border-context-6 p-4"
}
const _hoisted_8 = { class: "t3 text-context-4" }
const _hoisted_9 = { class: "t2 mb-0 flex items-center" }
const _hoisted_10 = {
  key: 2,
  class: "border-b border-context-6 p-4"
}
const _hoisted_11 = { class: "border-b border-context-6 p-4" }
const _hoisted_12 = {
  key: 3,
  class: "border-b border-context-6 p-4"
}
const _hoisted_13 = { class: "t3 text-context-4" }
const _hoisted_14 = { class: "mb-0" }
const _hoisted_15 = {
  key: 0,
  class: "ml-1"
}
const _hoisted_16 = { class: "border-b border-context-6 p-4" }
const _hoisted_17 = { class: "t3 text-context-4" }
const _hoisted_18 = { key: 0 }
const _hoisted_19 = {
  key: 4,
  class: "border-b border-context-6 p-4"
}
const _hoisted_20 = { class: "t3 text-context-4" }
const _hoisted_21 = { class: "border-b border-context-6 p-4" }
const _hoisted_22 = { class: "t3 text-context-4" }
const _hoisted_23 = { class: "mb-4" }
const _hoisted_24 = { class: "t3 text-context-4" }

import { computed } from 'vue'
import { type LocationAsRelativeRaw } from 'vue-router'
import i18n from '@/scripts/app-configs/i18n-config'
import AllMemberKits from '@/components/AllMemberKits.vue'
import PatientGoals from '@/components/drawers/MemberDrawer/MemberDrawerContent/MemberDrawerContentPatientGoals.vue'
import PatientReminders from '@/components/drawers/MemberDrawer/MemberDrawerContent/MemberDrawerContentPatientReminders.vue'
import WidgetLink from '@/components/drawers/MemberDrawer/MemberDrawerContent/MemberDrawerContentWidgetLink.vue'
import PatientContacts from '@/components/drawers/MemberDrawer/MemberDrawerContent/MemberDrawerContentPatientContacts.vue'
import PatientPrescription from '@/components/drawers/MemberDrawer/MemberDrawerContent/MemberDrawerContentPatientPrescription.vue'
import MemberDrawerContentHeader from '@/components/drawers/MemberDrawer/MemberDrawerContent/MemberDrawerContentHeader.vue'

import { PATIENT_UNIT as UNIT } from '@/scripts/configs/patient-units'
import { LeaveFeedEventDestination, FeedEventSource } from '@/utils/segment/segment.types'
import type {
  Profile,
  CareCoordinator,
  Reminder,
  Goal,
  ClinicalInformation,
  MemberDrawerProtocolInfo,
  Contact,
  MemberDrawerProtocol,
  Kit,
  Eligibility,
  Context,
} from '@/components/drawers/MemberDrawer/types/member-drawer.types'

interface MemberDrawerContentProps {
  profileInfo: Profile
  therapistId: number
  careCoordinators: CareCoordinator[]
  reminders: Reminder[]
  goals: Goal[]
  clinicalInfo?: Partial<ClinicalInformation>
  protocol?: MemberDrawerProtocolInfo
  appVersion: string
  age?: number | string
  currentState: string | null
  clinicalPortalBaseUrl?: string
  contacts?: Contact[]
  bloomProtocol: MemberDrawerProtocol
  kitHistory: Kit[]
  eligibility?: Eligibility
}


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    profileInfo: {},
    therapistId: {},
    careCoordinators: {},
    reminders: {},
    goals: {},
    clinicalInfo: { default: () => ({}) },
    protocol: {},
    appVersion: {},
    age: {},
    currentState: {},
    clinicalPortalBaseUrl: {},
    contacts: { default: () => [] },
    bloomProtocol: {},
    kitHistory: {},
    eligibility: { default: () => ({}) }
  },
  emits: ["close-side-bar", "copy-to-clipboard", "open-ui", "change-attention-level", "send-to-user", "leave-feed", "start-call"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const userIsLead = computed(() => {
  const authUserID = props.therapistId
  const leadID = props.profileInfo.lead_id
  const ownerID = props.profileInfo.owner?.id

  return authUserID === leadID && authUserID !== ownerID
})

const kitText = computed(() => {
  const { clinicalInfo, profileInfo } = props

  if (clinicalInfo?.no_pod) {
    return i18n.t('SIDEBAR.KIT_BLOOM_NO-POD')
  }

  if (profileInfo.unit === UNIT.BLOOM && clinicalInfo?.bloom_pod_version) {
    return i18n.t(clinicalInfo.bloom_pod_version)
  }

  if (clinicalInfo?.kit) {
    return i18n.t(clinicalInfo.kit)
  }

  return ''
})

const prescriptionsRoute = (): LocationAsRelativeRaw => {
  const { clinicalInfo, profileInfo } = props

  switch (profileInfo.unit) {
    case UNIT.MOVE:
      return { name: 'profile.move-prescriptions', params: { patientID: profileInfo.user_id } }

    case UNIT.BLOOM:
    case UNIT.MIND:
      return { name: 'prescriptions', params: { memberId: profileInfo.user_id } }

    default:
      const hasSessions = clinicalInfo.total_non_default_sessions || profileInfo?.has_pending_prescription

      return { name: hasSessions ? 'profile.sessions' : 'profile.treatmentPath', params: { patientID: profileInfo.user_id } }
  }
}

const navigationMetadata = computed(() => {
  const { user_id: userId, unit } = props.profileInfo

  return [
    {
      label: 'COMMON.PRESCRIPTIONS',
      route: prescriptionsRoute(),
      icon: 'prescription',
      disabled: userIsLead.value,
      eventDestination: LeaveFeedEventDestination.PRESCRIPTION,
    },
    {
      label: 'copy_2681',
      route: { name: unit === UNIT.BLOOM ? 'therapy_results.clinical-sessions' : 'therapy_results.sessions', params: { patientID: userId } },
      icon: 'prescription-results',
      disabled: userIsLead.value,
      hidden: unit === UNIT.MIND || unit === UNIT.MOVE,
      eventDestination: LeaveFeedEventDestination.RESULTS,
    },
    {
      label: 'clinical_records',
      route: { name: 'clinical-records.listing', params: { patientID: userId } },
      icon: 'forms',
      disabled: userIsLead.value,
      eventDestination: LeaveFeedEventDestination.RECORDS,
    },
    {
      label: 'copy_2952',
      route: { name: 'profile.details', params: { patientID: userId } },
      icon: 'user',
      disabled: userIsLead.value,
      eventDestination: LeaveFeedEventDestination.PROFILE,
    },
  ].filter((item) => !item.hidden)
})

const handleWidgetLinkClick = (item: { eventDestination: LeaveFeedEventDestination }) => {
  emit('leave-feed', {
    source: FeedEventSource.SIDEBAR,
    destination: item.eventDestination,
  })
}

const copyContentToClipboard = (context: Context, content: string) => {
  console.log(context, content)

  emit('copy-to-clipboard', { context, content })
}

const startCall = (phoneNumber: string) => {
  window.open(`zoomphonecall://${phoneNumber.replace(' ', '')}`)
  emit('start-call', { source: FeedEventSource.SIDEBAR })
}

return (_ctx: any,_cache: any) => {
  const _component_sword_button_wuk = _resolveComponent("sword-button-wuk")!
  const _component_kit_status_pill = _resolveComponent("kit-status-pill")!
  const _component_temporary_transfer_pill = _resolveComponent("temporary-transfer-pill")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_sword_button_wuk, {
      class: "absolute right-1 top-1",
      theme: "text",
      round: "",
      size: "small",
      icon: "close",
      onClickNative: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close-side-bar')))
    }),
    _createVNode(MemberDrawerContentHeader, {
      profile: __props.profileInfo,
      age: __props.age,
      "current-state": __props.currentState,
      eligibility: __props.eligibility,
      onCopyToClipboard: _cache[1] || (_cache[1] = (context) => emit('copy-to-clipboard', context)),
      onOpenUi: _cache[2] || (_cache[2] = (context) => emit('open-ui', context)),
      onSendToUser: _cache[3] || (_cache[3] = (context) => emit('send-to-user', context))
    }, null, 8, ["profile", "age", "current-state", "eligibility"]),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("section", _hoisted_3, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(navigationMetadata.value, (item) => {
          return (_openBlock(), _createBlock(WidgetLink, {
            key: `navigation-${_ctx.$t(item.label)}`,
            label: item.label,
            icon: item.icon,
            route: item.route,
            disabled: item.disabled,
            target: "_blank",
            rel: "noreferrer noopener",
            onWidgetLinkClick: ($event: any) => (handleWidgetLinkClick(item))
          }, null, 8, ["label", "icon", "route", "disabled", "onWidgetLinkClick"]))
        }), 128))
      ]),
      (__props.profileInfo.unit !== _unref(UNIT).MIND && !!__props.clinicalInfo?.therapy_name)
        ? (_openBlock(), _createElementBlock("section", _hoisted_4, [
            _createElementVNode("h6", _hoisted_5, [
              _createElementVNode("span", null, _toDisplayString(_ctx.$t(__props.clinicalInfo.therapy_name)), 1),
              (!!__props.clinicalInfo.side)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _cache[7] || (_cache[7] = _createElementVNode("span", { class: "dot-separator mx-2" }, null, -1)),
                    _createElementVNode("span", null, _toDisplayString(_ctx.$t(__props.clinicalInfo.side)), 1)
                  ], 64))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("p", _hoisted_6, _toDisplayString(_ctx.$t(__props.clinicalInfo.condition || '')), 1)
          ]))
        : _createCommentVNode("", true),
      (__props.profileInfo.unit !== _unref(UNIT).MIND && (__props.clinicalInfo?.kit || __props.clinicalInfo?.kit_status))
        ? (_openBlock(), _createElementBlock("section", _hoisted_7, [
            _createElementVNode("div", null, [
              _createElementVNode("h6", _hoisted_8, _toDisplayString(_ctx.$t("SIDEBAR.KIT")), 1),
              _createElementVNode("p", _hoisted_9, [
                _createTextVNode(_toDisplayString(kitText.value) + " ", 1),
                (__props.clinicalInfo?.kit_status && !__props.clinicalInfo?.no_pod)
                  ? (_openBlock(), _createBlock(_component_kit_status_pill, {
                      key: 0,
                      class: "ml-2",
                      status: __props.clinicalInfo.kit_status
                    }, null, 8, ["status"]))
                  : _createCommentVNode("", true)
              ])
            ]),
            (__props.kitHistory.length > 1)
              ? (_openBlock(), _createBlock(AllMemberKits, {
                  key: 0,
                  items: __props.kitHistory
                }, null, 8, ["items"]))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true),
      (__props.profileInfo.unit !== _unref(UNIT).MIND)
        ? (_openBlock(), _createElementBlock("section", _hoisted_10, [
            _createVNode(PatientPrescription, {
              unit: __props.profileInfo.unit,
              protocol: __props.protocol,
              "bloom-program-name": __props.bloomProtocol?.bloom_program_name,
              "no-pod-sessions-allowed": __props.clinicalInfo?.no_pod_sessions_allowed
            }, null, 8, ["unit", "protocol", "bloom-program-name", "no-pod-sessions-allowed"]),
            _createVNode(PatientReminders, { items: __props.reminders }, null, 8, ["items"]),
            _createVNode(PatientGoals, {
              items: __props.goals,
              onCopyToClipboard: _cache[4] || (_cache[4] = ($event) => copyContentToClipboard('goals', $event))
            }, null, 8, ["items"])
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("section", _hoisted_11, [
        _createVNode(PatientContacts, {
          contacts: __props.contacts,
          onStartCall: _cache[5] || (_cache[5] = (phoneNumber) => startCall(phoneNumber)),
          onCopyToClipboard: _cache[6] || (_cache[6] = ({context, content}) => copyContentToClipboard(context, content))
        }, null, 8, ["contacts"])
      ]),
      (__props.profileInfo.owner?.name)
        ? (_openBlock(), _createElementBlock("section", _hoisted_12, [
            _createElementVNode("h6", _hoisted_13, _toDisplayString(_ctx.$t("copy_2992")), 1),
            _createElementVNode("p", _hoisted_14, [
              _createTextVNode(_toDisplayString(__props.profileInfo.owner.name) + " ", 1),
              (__props.profileInfo.owner?.id === __props.therapistId)
                ? (_openBlock(), _createElementBlock("span", _hoisted_15, "(" + _toDisplayString(_ctx.$t("COMMON.you")) + ")", 1))
                : _createCommentVNode("", true)
            ]),
            (__props.profileInfo.owner.temporary_transfer)
              ? (_openBlock(), _createBlock(_component_temporary_transfer_pill, {
                  key: 0,
                  class: "mt-1",
                  "start-date": __props.profileInfo.owner.temporary_transfer.start_date,
                  "end-date": __props.profileInfo.owner.temporary_transfer.end_date
                }, null, 8, ["start-date", "end-date"]))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("section", _hoisted_16, [
        _createElementVNode("h6", _hoisted_17, _toDisplayString(_ctx.$t("INTAKE.PT")), 1),
        _createElementVNode("p", null, [
          _createTextVNode(_toDisplayString(__props.profileInfo.intake_pt?.name || "-") + " ", 1),
          (__props.profileInfo.intake_pt?.id === __props.therapistId)
            ? (_openBlock(), _createElementBlock("span", _hoisted_18, "(" + _toDisplayString(_ctx.$t("COMMON.you")) + ")", 1))
            : _createCommentVNode("", true)
        ])
      ]),
      (__props.careCoordinators?.length)
        ? (_openBlock(), _createElementBlock("section", _hoisted_19, [
            _createElementVNode("h6", _hoisted_20, _toDisplayString(_ctx.$t("ASSIGNED_CC_KEY")), 1),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.careCoordinators, (cc, ccIndex) => {
              return (_openBlock(), _createElementBlock("li", { key: ccIndex }, _toDisplayString(cc?.firstname) + " " + _toDisplayString(cc?.lastname), 1))
            }), 128))
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("section", _hoisted_21, [
        (__props.profileInfo.unit === _unref(UNIT).SWORD)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createElementVNode("h6", _hoisted_22, _toDisplayString(_ctx.$t('SIDEBAR.DT_VERSION')), 1),
              _createElementVNode("p", _hoisted_23, _toDisplayString(__props.clinicalInfo.apk_version || '-'), 1)
            ], 64))
          : _createCommentVNode("", true),
        _createElementVNode("h6", _hoisted_24, _toDisplayString(_ctx.$t('SIDEBAR.MEMBER_APP_VERSION')), 1),
        _createElementVNode("p", null, _toDisplayString(__props.appVersion || '-'), 1)
      ])
    ])
  ]))
}
}

})